import { useEffect, useState } from "react";
import { CalendarFeedCard } from "@/ui/calendar-feed";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import QRCode from "qrcode";
import { api, type ApiError } from "@/lib/api";
import { TelegramCard } from "@/ui/telegram";
import { AvatarEditor, DisplayPrefsCard } from "@/ui/branding";
import { useLookups } from "@/lib/catalog";

export interface Me {
  identity: {
    id: string;
    email: string;
    displayName: string;
    totpEnabled: boolean;
    backupCodesRemaining: number;
  };
  tenant: { id: string; slug: string; displayName: string };
  sessionId: string;
}

export function useMe() {
  return useQuery({ queryKey: ["auth", "me"], queryFn: () => api.get<Me>("/auth/me") });
}

export function AccountPage(): JSX.Element {
  const { data: me, isLoading } = useMe();
  const { me: cme } = useLookups();
  if (isLoading || !me) return <div className="card compact muted">Carregando…</div>;

  const mfaForced = new URLSearchParams(window.location.search).get("mfa") === "obrigatorio" && !me.identity.totpEnabled;
  return (
    <div className="stack">
      {mfaForced && <div className="banner-bad">A política do escritório exige a <b>verificação em duas etapas</b> para o seu papel. Ative abaixo para voltar a usar o sistema.</div>}
      <div className="card compact">
        <h2>Minha conta</h2>
        <div style={{ margin: "8px 0 14px" }}><AvatarEditor userId={cme?.id} name={me.identity.displayName} /></div>
        <dl className="kv">
          <dt>Nome</dt><dd>{me.identity.displayName}</dd>
          <dt>E-mail</dt><dd>{me.identity.email}</dd>
          <dt>Escritório</dt><dd>{me.tenant.displayName}</dd>
        </dl>
      </div>
      <DisplayPrefsCard />
      <NotifPrefsCard />
      <TelegramCard />
      <CalendarFeedCard />
      <MfaCard me={me} />
      {me.identity.totpEnabled && <TrustedDevicesCard />}
      <ChangePasswordCard />
    </div>
  );
}

// ---------------------------------------------------------------- 2FA

type MfaStep = "idle" | "scan" | "codes" | "disable";

function MfaCard({ me }: { me: Me }): JSX.Element {
  const qc = useQueryClient();
  const [step, setStep] = useState<MfaStep>("idle");
  const [setup, setSetup] = useState<{ secret: string; otpauthUrl: string } | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (setup) void QRCode.toDataURL(setup.otpauthUrl, { margin: 1, width: 200 }).then(setQr);
  }, [setup]);

  const run = async (fn: () => Promise<void>): Promise<void> => {
    setError(null);
    setLoading(true);
    try { await fn(); } catch (e) { setError((e as ApiError).message); } finally { setLoading(false); }
  };

  const start = (): Promise<void> => run(async () => {
    setSetup(await api.post<{ secret: string; otpauthUrl: string }>("/auth/mfa/setup"));
    setCode("");
    setStep("scan");
  });

  const enable = (): Promise<void> => run(async () => {
    const r = await api.post<{ backupCodes: string[] }>("/auth/mfa/enable", { code });
    setBackupCodes(r.backupCodes);
    setSetup(null);
    setQr(null);
    setStep("codes");
    await qc.invalidateQueries({ queryKey: ["auth", "me"] });
  });

  const disable = (): Promise<void> => run(async () => {
    await api.post("/auth/mfa/disable", { code });
    setCode("");
    setStep("idle");
    await qc.invalidateQueries({ queryKey: ["auth", "me"] });
  });

  return (
    <div className="card compact">
      <h2>Verificação em duas etapas (2FA)</h2>

      {step === "idle" && !me.identity.totpEnabled && (
        <>
          <p className="muted">
            Desativada. Com o 2FA, além da senha é pedido um código do seu celular
            (Google Authenticator, Microsoft Authenticator, 1Password etc.).
          </p>
          <button className="btn primary" onClick={() => void start()} disabled={loading}>
            Ativar 2FA
          </button>
        </>
      )}

      {step === "idle" && me.identity.totpEnabled && (
        <>
          <p><span className="badge ok">Ativa</span></p>
          <p className="muted small">
            Códigos de backup restantes: <strong>{me.identity.backupCodesRemaining}</strong>
          </p>
          <button className="btn" onClick={() => { setCode(""); setStep("disable"); }}>
            Desativar 2FA
          </button>
        </>
      )}

      {step === "scan" && setup && (
        <form className="stack" onSubmit={(e) => { e.preventDefault(); void enable(); }}>
          <p className="muted">
            1. Escaneie o QR code no aplicativo autenticador.<br />
            2. Digite o código de 6 dígitos que aparecer.
          </p>
          {qr && <img src={qr} alt="QR code do 2FA" width={200} height={200} className="qr" />}
          <p className="muted small">
            Sem câmera? Digite a chave manualmente: <code>{setup.secret}</code>
          </p>
          <label className="field">
            Código
            <input inputMode="numeric" maxLength={6} autoFocus value={code}
              onChange={(e) => setCode(e.target.value)} />
          </label>
          <div className="row">
            <button type="submit" className="btn primary" disabled={loading || code.length !== 6}>
              Confirmar e ativar
            </button>
            <button type="button" className="btn ghost" onClick={() => setStep("idle")}>Cancelar</button>
          </div>
        </form>
      )}

      {step === "codes" && (
        <div className="stack">
          <div className="success">2FA ativada.</div>
          <p>
            <strong>Guarde estes códigos de backup agora.</strong> Cada um vale uma vez e
            serve para entrar se você perder o celular. Eles não serão mostrados de novo.
          </p>
          <div className="backup-codes">
            {backupCodes.map((c) => <code key={c}>{c}</code>)}
          </div>
          <div className="row">
            <button className="btn" onClick={() => void navigator.clipboard.writeText(backupCodes.join("\n"))}>
              Copiar códigos
            </button>
            <button className="btn primary" onClick={() => { setBackupCodes([]); setStep("idle"); }}>
              Já guardei
            </button>
          </div>
        </div>
      )}

      {step === "disable" && (
        <form className="stack" onSubmit={(e) => { e.preventDefault(); void disable(); }}>
          <p className="muted">Para desativar, confirme com um código do aplicativo ou de backup.</p>
          <label className="field">
            Código
            <input autoFocus maxLength={16} value={code} onChange={(e) => setCode(e.target.value)} />
          </label>
          <div className="row">
            <button type="submit" className="btn primary" disabled={loading || code.trim().length < 6}>
              Desativar
            </button>
            <button type="button" className="btn ghost" onClick={() => setStep("idle")}>Cancelar</button>
          </div>
        </form>
      )}

      {error && <div className="error">{error}</div>}
    </div>
  );
}

// ---------------------------------------------------------------- senha

function ChangePasswordCard(): JSX.Element {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setMsg(null);
    if (next !== confirm) { setMsg({ ok: false, text: "As senhas novas não conferem." }); return; }
    setLoading(true);
    try {
      await api.post("/auth/password/change", { currentPassword: current, newPassword: next });
      setCurrent(""); setNext(""); setConfirm("");
      setMsg({ ok: true, text: "Senha alterada. Suas outras sessões foram encerradas." });
    } catch (err) {
      setMsg({ ok: false, text: (err as ApiError).message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card compact">
      <h2>Trocar senha</h2>
      <form className="stack" onSubmit={(e) => void submit(e)}>
        <label className="field">
          Senha atual
          <input type="password" required autoComplete="current-password"
            value={current} onChange={(e) => setCurrent(e.target.value)} />
        </label>
        <label className="field">
          Nova senha (mínimo 10 caracteres)
          <input type="password" required minLength={10} autoComplete="new-password"
            value={next} onChange={(e) => setNext(e.target.value)} />
        </label>
        <label className="field">
          Confirme a nova senha
          <input type="password" required minLength={10} autoComplete="new-password"
            value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </label>
        {msg && <div className={msg.ok ? "success" : "error"}>{msg.text}</div>}
        <div className="row">
          <button type="submit" className="btn primary" disabled={loading}>
            {loading ? "Salvando..." : "Trocar senha"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------- avisos

function NotifPrefsCard(): JSX.Element {
  const qc = useQueryClient();
  const [err, setErr] = useState<string | null>(null);
  const { data } = useQuery({ queryKey: ["notif-prefs"], queryFn: () => api.get<{ resumoDiario: boolean; fatal24h: boolean; emailAtribuicao: boolean; whatsappFatal: boolean; whatsappResumo: boolean; whatsappPhone: string; email: string }>("/notificacoes/preferencias") });
  const [phone, setPhone] = useState<string | null>(null);
  if (!data) return <div className="card compact muted">Carregando avisos…</div>;
  const set = async (patch: Partial<typeof data>) => {
    setErr(null);
    try {
      const { email: _e, ...rest } = { ...data, ...patch };
      await api.put("/notificacoes/preferencias", rest);
      await qc.invalidateQueries({ queryKey: ["notif-prefs"] });
    } catch (e) { setErr((e as Error).message); }
  };
  return (
    <div className="card compact">
      <h2>Avisos por e-mail</h2>
      <div className="muted small" style={{ marginBottom: 8 }}>Enviados para {data.email}. Os avisos do sino (atribuições, revisões, intimações, prazo fatal) aparecem sempre.</div>
      <label className="row nowrap"><input type="checkbox" checked={data.resumoDiario} onChange={(e) => void set({ resumoDiario: e.target.checked })} /> Resumo do dia às 7h (dias úteis): prazos fatais, atrasadas, o que é para hoje e audiências</label>
      <label className="row nowrap" style={{ marginTop: 6 }}><input type="checkbox" checked={data.fatal24h} onChange={(e) => void set({ fatal24h: e.target.checked })} /> Aviso no dia útil anterior ao prazo fatal</label>
      <h2 style={{ marginTop: 16 }}>WhatsApp</h2>
      <div className="muted small" style={{ marginBottom: 8 }}>Só funciona quando o escritório ligar o WhatsApp em Configurações › Integrações.</div>
      <div className="row nowrap">
        <input className="input" style={{ maxWidth: 200 }} placeholder="(87) 99999-0000" value={phone ?? data.whatsappPhone} onChange={(e) => setPhone(e.target.value)} />
        <button className="btn small" disabled={phone === null || phone === data.whatsappPhone} onClick={() => void set({ whatsappPhone: phone ?? "" }).then(() => setPhone(null))}>Salvar número</button>
      </div>
      <label className="row nowrap" style={{ marginTop: 6 }}><input type="checkbox" checked={data.whatsappFatal} disabled={!data.whatsappPhone} onChange={(e) => void set({ whatsappFatal: e.target.checked })} /> Prazo fatal no dia útil anterior</label>
      <label className="row nowrap" style={{ marginTop: 6 }}><input type="checkbox" checked={data.whatsappResumo} disabled={!data.whatsappPhone} onChange={(e) => void set({ whatsappResumo: e.target.checked })} /> Resumo do dia às 7h</label>
      {err && <div className="error">{err}</div>}
    </div>
  );
}

// ---------------------------------------------------------------- navegadores confiáveis

function TrustedDevicesCard(): JSX.Element {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["auth", "dispositivos"], queryFn: () => api.get<Array<{ id: string; label: string | null; ip: string | null; createdAt: string; lastUsedAt: string; expiresAt: string }>>("/auth/dispositivos") });
  const [busy, setBusy] = useState(false);
  const forget = async (id?: string) => {
    setBusy(true);
    try { await api.post("/auth/dispositivos/esquecer", id ? { id } : {}); await qc.invalidateQueries({ queryKey: ["auth", "dispositivos"] }); } finally { setBusy(false); }
  };
  const d = (s: string) => new Date(s).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
  return (
    <div className="card compact">
      <h3>Navegadores confiáveis</h3>
      <p className="muted small">Ao entrar com “Manter conectado” marcado e digitar o código, o navegador fica 15 dias sem pedir o código de 2 etapas. Trocar a senha ou mexer no 2FA desfaz todos.</p>
      {!data ? <div className="muted small">Carregando…</div> : !data.length ? <div className="muted small">Nenhum navegador confiável.</div> : (
        <table className="table small"><thead><tr><th>Navegador</th><th>Último uso</th><th>Vale até</th><th /></tr></thead><tbody>
          {data.map((x) => <tr key={x.id}><td>{x.label ?? "Navegador"}{x.ip ? <span className="muted"> · {x.ip}</span> : null}</td><td>{d(x.lastUsedAt)}</td><td>{d(x.expiresAt)}</td><td><button className="btn small" disabled={busy} onClick={() => void forget(x.id)}>Esquecer</button></td></tr>)}
        </tbody></table>
      )}
      {!!data?.length && <button className="btn small" style={{ marginTop: 8 }} disabled={busy} onClick={() => void forget()}>Esquecer todos</button>}
    </div>
  );
}
